/**
 * Complete Production-Grade Inventory & Stock Control Route — Module 4.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { siteConfig } from "@/config/site";
import {
  Boxes,
  AlertTriangle,
  Plus,
  Minus,
  RefreshCw,
  History,
  Download,
  IndianRupee,
  CheckCircle2,
  XCircle,
  Check,
  PackageCheck,
} from "lucide-react";
import { formatPrice } from "@/components/forms/price-display";
import { DataTable, type ColumnDef } from "@/components/admin/common/data-table";
import {
  fetchAdminProductsFromSupabase,
  type ExtendedProduct,
} from "@/features/admin/products/store/admin-products-store";
import {
  getStoredInventoryLogs,
  saveStoredInventoryLogs,
  fetchInventoryLogsFromSupabase,
  adjustProductStock,
  type StockMovementLog,
  type MovementType,
} from "@/features/admin/inventory/store/admin-inventory-store";
import { StockAdjustmentModal } from "@/components/admin/inventory/stock-adjustment-modal";
import { StockTimelineDrawer } from "@/components/admin/inventory/stock-timeline-drawer";

export const Route = createFileRoute("/admin/inventory")({
  head: () => ({
    meta: [{ title: `Inventory Control — Admin — ${siteConfig.name}` }],
  }),
  component: AdminInventoryPage,
});

function AdminInventoryPage() {
  const [products, setProducts] = useState<ExtendedProduct[]>([]);
  const [logs, setLogs] = useState<StockMovementLog[]>(() => getStoredInventoryLogs());
  const [filterTab, setFilterTab] = useState<"ALL" | "LOW_STOCK" | "OUT_OF_STOCK" | "HEALTHY">(
    "ALL",
  );

  // Modal & Drawer State
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ExtendedProduct | null>(null);

  const [timelineOpen, setTimelineOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const loadData = () => {
    // 1. Instant local render (removed products fallback)
    const movementLogs = getStoredInventoryLogs();
    setLogs(movementLogs);

    // 2. Fetch live data from Supabase
    fetchAdminProductsFromSupabase().then((dbProds) => {
      if (dbProds && dbProds.length > 0) setProducts(dbProds);
    });
    fetchInventoryLogsFromSupabase().then((dbLogs) => {
      if (dbLogs && dbLogs.length > 0) setLogs(dbLogs);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const triggerToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const handleOpenAdjust = (prod?: ExtendedProduct, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedProduct(prod || null);
    setAdjustModalOpen(true);
  };

  const handleAdjustSubmit = async (
    productId: string,
    quantityChange: number,
    type: MovementType,
    reason: string,
    supplierName?: string,
    poNumber?: string,
  ) => {
    try {
      const updated = await adjustProductStock(
        productId,
        quantityChange,
        type,
        reason,
        supplierName,
        poNumber,
      );
      setProducts(updated);
      setLogs(getStoredInventoryLogs());
      triggerToast("Stock adjustment logged and inventory updated!");
    } catch (err: unknown) {
      console.error("Stock adjustment failed:", err);
      const message = (err as { message?: string }).message;
      triggerToast(`Failed to update stock in database: ${message}`);
    }
  };

  // Filtered Inventory List
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (filterTab === "LOW_STOCK")
        return p.stockQuantity > 0 && p.stockQuantity <= (p.minStockThreshold || 5);
      if (filterTab === "OUT_OF_STOCK") return p.stockQuantity === 0;
      if (filterTab === "HEALTHY") return p.stockQuantity > (p.minStockThreshold || 5);
      return true;
    });
  }, [products, filterTab]);

  // Inventory Summary Metrics
  const stats = useMemo(() => {
    const totalUnits = products.reduce((acc, p) => acc + p.stockQuantity, 0);
    const costValuation = products.reduce(
      (acc, p) => acc + (p.costPrice || p.price * 0.5) * p.stockQuantity,
      0,
    );
    const retailValuation = products.reduce((acc, p) => acc + p.price * p.stockQuantity, 0);
    const lowStockCount = products.filter(
      (p) => p.stockQuantity > 0 && p.stockQuantity <= (p.minStockThreshold || 5),
    ).length;
    const outOfStockCount = products.filter((p) => p.stockQuantity === 0).length;

    return { totalUnits, costValuation, retailValuation, lowStockCount, outOfStockCount };
  }, [products]);

  // Low Stock Items List for Alert Banner
  const lowStockAlertItems = useMemo(
    () =>
      products.filter(
        (p) => p.stockQuantity <= (p.minStockThreshold || 5) || p.stockQuantity === 0,
      ),
    [products],
  );

  // Table Columns
  const columns: ColumnDef<ExtendedProduct>[] = [
    {
      id: "name",
      header: "Product Item",
      accessorKey: "name",
      cell: (row) => (
        <div className="flex items-center gap-3.5">
          <img
            src={row.image}
            alt={row.name}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = "/plantpicture/p1/5.webp";
            }}
            className="h-12 w-12 object-cover bg-secondary rounded-xl border shrink-0 shadow-2xs"
          />
          <div>
            <p className="font-bold text-foreground text-sm">{row.name}</p>
            <p className="text-xs text-muted-foreground font-mono">SKU: {row.sku}</p>
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      id: "category",
      header: "Category",
      accessorKey: "category",
      cell: (row) => (
        <span className="text-muted-foreground text-sm font-medium">{row.category}</span>
      ),
      sortable: true,
    },
    {
      id: "stockQuantity",
      header: "Stock Quantity",
      accessorKey: "stockQuantity",
      cell: (row) => {
        const isOut = row.stockQuantity === 0;
        const isLow = row.stockQuantity <= (row.minStockThreshold || 5);

        return (
          <div className="space-y-0.5">
            <span className="font-display font-bold text-base text-foreground block tabular-nums">
              {row.stockQuantity} units
            </span>
            <span
              className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                isOut
                  ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                  : isLow
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                    : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
              }`}
            >
              {isOut ? "OUT OF STOCK" : isLow ? "LOW STOCK" : "HEALTHY"}
            </span>
          </div>
        );
      },
      sortable: true,
    },
    {
      id: "minStockThreshold",
      header: "Alert Threshold",
      accessorKey: "minStockThreshold",
      cell: (row) => (
        <span className="font-mono text-xs font-semibold text-muted-foreground">
          Min: {row.minStockThreshold || 5} units
        </span>
      ),
      sortable: true,
    },
    {
      id: "costPrice",
      header: "Unit Cost / Price",
      accessorKey: "costPrice",
      cell: (row) => (
        <div>
          <span className="font-display font-bold tabular-nums text-foreground block text-sm">
            {formatPrice(row.price)}
          </span>
          <span className="text-xs text-muted-foreground font-mono">
            Cost: {formatPrice(row.costPrice || 0)}
          </span>
        </div>
      ),
      sortable: true,
    },
  ];

  return (
    <div className="space-y-6 pb-16">
      {/* Toast Alert */}
      {toastMsg && (
        <div className="fixed top-20 right-6 z-50 bg-foreground text-background text-xs font-semibold px-4 py-3 rounded-md shadow-xl flex items-center gap-2 animate-in fade-in duration-150">
          <Check className="h-4 w-4 text-emerald-400" />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-primary font-semibold">Warehouse Telemetry</p>
          <h1 className="mt-1 font-display text-3xl text-foreground">Stock & Inventory Control</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Real-time warehouse stock tracking, purchase orders, reorder threshold alerts, and audit
            logs.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setTimelineOpen(true)}
            className="inline-flex min-h-10 items-center gap-2 border border-border bg-card px-3 text-xs font-semibold text-foreground rounded hover:bg-secondary transition-colors"
          >
            <History className="h-4 w-4 text-primary" /> Movement Logs ({logs.length})
          </button>
          <button
            onClick={() => handleOpenAdjust()}
            className="inline-flex min-h-10 items-center gap-2 bg-primary px-4 text-xs font-semibold uppercase tracking-wider text-primary-foreground rounded hover:opacity-90 transition-opacity shadow-xs"
          >
            <Boxes className="h-4 w-4" /> Quick Restock
          </button>
        </div>
      </div>

      {/* Summary Metric Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Total Units In Stock
          </span>
          <span className="font-display text-xl font-bold text-foreground">
            {stats.totalUnits.toLocaleString("en-IN")} units
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Inventory Cost Valuation
          </span>
          <span className="font-display text-xl font-bold text-foreground">
            {formatPrice(stats.costValuation)}
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Retail Sales Valuation
          </span>
          <span className="font-display text-xl font-bold text-emerald-700">
            {formatPrice(stats.retailValuation)}
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">Low Stock Alerts</span>
          <span className="font-display text-xl font-bold text-amber-700">
            {stats.lowStockCount} Products
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Out of Stock Count
          </span>
          <span className="font-display text-xl font-bold text-rose-700">
            {stats.outOfStockCount} Products
          </span>
        </div>
      </div>

      {/* Low Stock Critical Alert Banner */}
      {lowStockAlertItems.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-amber-900">
                {lowStockAlertItems.length} Products Require Immediate Warehouse Reordering
              </p>
              <p className="text-[11px] text-amber-700">
                Inventory levels have breached minimum reorder safety thresholds.
              </p>
            </div>
          </div>

          <button
            onClick={() => handleOpenAdjust(lowStockAlertItems[0])}
            className="px-3.5 py-1.5 bg-amber-700 text-white rounded text-xs font-semibold hover:bg-amber-800 transition-colors shrink-0 shadow-2xs"
          >
            Restock Item Now
          </button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-border pb-3 text-xs overflow-x-auto">
        {[
          { key: "ALL", label: `All Items (${products.length})` },
          { key: "LOW_STOCK", label: `Low Stock Alerts (${stats.lowStockCount})` },
          { key: "OUT_OF_STOCK", label: `Out of Stock (${stats.outOfStockCount})` },
          { key: "HEALTHY", label: "Healthy Stock" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() =>
              setFilterTab(tab.key as "ALL" | "LOW_STOCK" | "OUT_OF_STOCK" | "HEALTHY")
            }
            className={`px-3.5 py-1.5 rounded transition-[color,background-color,border-color,box-shadow,transform,opacity] font-medium ${
              filterTab === tab.key
                ? "bg-primary text-primary-foreground font-semibold shadow-2xs"
                : "bg-card border border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Enterprise Data Table */}
      <DataTable
        data={filteredProducts}
        columns={columns}
        keyExtractor={(row) => row.id}
        searchPlaceholder="Search inventory by title, category, or SKU..."
        searchField={(row) => `${row.name} ${row.category} ${row.sku}`}
        onRowClick={(product) => handleOpenAdjust(product)}
        bulkActions={[
          {
            label: "Bulk Restock (+10 units)",
            onClick: (selected) => {
              selected.forEach((p) => {
                adjustProductStock(p.id, 10, "RESTOCK", "Bulk Restock Action", "Warehouse Bulk PO");
              });
              loadData();
              triggerToast(`Restocked +10 units across ${selected.length} items.`);
            },
          },
        ]}
      />

      {/* Stock Adjustment Modal */}
      <StockAdjustmentModal
        open={adjustModalOpen}
        product={selectedProduct}
        allProducts={products}
        onClose={() => setAdjustModalOpen(false)}
        onAdjust={handleAdjustSubmit}
      />

      {/* Stock Timeline Drawer */}
      <StockTimelineDrawer open={timelineOpen} logs={logs} onClose={() => setTimelineOpen(false)} />
    </div>
  );
}
